import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Volume2, VolumeX, X } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const DWELL_MS = 45000; // hard cap per segment even if the video loops

// TV Mode — lean-back broadcast channel (Eric, 2026-08-30).
// Autoplays the geolocated feed like an old-school station: full-bleed
// video, lower-third chyron, ON AIR ident, clock. AirPlay-friendly
// (the <video> is a plain element — use the iOS share/AirPlay controls).
export default function TVPage() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [clock, setClock] = useState("");
  const videoRef = useRef(null);
  const dwellRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/videos/map`).then((r) => {
      const list = (r.data || []).filter((v) => v.video_url);
      setVideos(list);
    }).catch(() => setVideos([]));
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setClock(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const next = useCallback(() => {
    setIdx((i) => (videos.length ? (i + 1) % videos.length : 0));
  }, [videos.length]);

  // Broken/dead-origin videos: skip forward instead of parking on black.
  const errCountRef = useRef(0);
  const onVideoError = useCallback(() => {
    errCountRef.current += 1;
    if (errCountRef.current <= (videos.length || 1)) {
      setTimeout(next, 600);
    }
  }, [next, videos.length]);

  useEffect(() => {
    clearTimeout(dwellRef.current);
    dwellRef.current = setTimeout(next, DWELL_MS);
    return () => clearTimeout(dwellRef.current);
  }, [idx, videos.length, next]);

  const v = videos[idx];
  return (
    <div className="fixed inset-0 z-[2500] bg-black text-white overflow-hidden" data-testid="tv-page">
      {v ? (
        <video
          ref={videoRef}
          key={v.id}
          src={v.video_url}
          autoPlay
          muted={muted}
          playsInline
          onEnded={next}
          onError={onVideoError}
          onPlaying={() => { errCountRef.current = 0; }}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="tv-video"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-gray-500">
          Tuning in…
        </div>
      )}
      {/* scanline/vignette texture for the broadcast feel */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)",
      }} />
      {/* Top bar — single safe-area-aware row so ident, ON AIR, and exit
          never collide with each other or the iOS status bar. */}
      <div
        className="absolute left-0 right-0 z-10 flex items-center justify-between gap-2 px-3"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
      >
        <div className="flex items-center gap-1.5 bg-black/50 backdrop-blur-sm rounded-full px-2.5 py-1.5 min-w-0">
          <Globe className="h-4 w-4 text-emerald-400 shrink-0" />
          <span className="text-[10px] font-bold tracking-[0.15em] truncate">GLOBALEYES·TV</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-1.5 bg-black/50 backdrop-blur-sm rounded-full px-2.5 py-1.5" data-testid="tv-on-air">
            <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[10px] font-semibold tracking-widest">ON AIR</span>
            <span className="text-[10px] text-gray-300 tabular-nums">{clock}</span>
          </div>
          <button
            onClick={() => navigate("/")}
            className="bg-black/50 backdrop-blur-sm rounded-full p-2"
            aria-label="Leave TV mode" data-testid="tv-exit-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      {/* Lower-third chyron */}
      {v && (
        <div className="absolute left-0 right-0 bottom-0 px-6" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 2rem)" }} data-testid="tv-chyron">
          <div className="max-w-2xl">
            <div className="inline-block bg-emerald-500 text-gray-950 text-[11px] font-bold tracking-widest px-2 py-0.5 mb-1">
              {(v.location?.city || "FIELD REPORT").toUpperCase()}
              {v.location?.country ? ` · ${v.location.country.toUpperCase()}` : ""}
            </div>
            <div className="bg-black/70 backdrop-blur-sm px-3 py-2">
              <p className="text-lg font-semibold leading-snug">{v.title}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                @{v.producer_username || v.username || "globaleyes"} · {idx + 1}/{videos.length}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Controls: tap video area advances; mute toggle */}
      <button
        onClick={next}
        className="absolute right-0 bottom-0 w-1/3"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 64px)" }}
        aria-label="Next segment" data-testid="tv-next-zone"
      />
      <button
        onClick={() => setMuted((m) => !m)}
        className="absolute bottom-8 right-6 bg-black/60 backdrop-blur-sm rounded-full p-3"
        aria-label="Toggle sound" data-testid="tv-mute-btn"
      >
        {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
      </button>
    </div>
  );
}
